import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type CustomerRow = {
  _id: string;
  name?: string;
  phone: string;
  email?: string | null;
  isActive: boolean;
  totalOrders?: number;
  deliveredOrders?: number;
  totalSpent?: number;
  lastOrderAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

const Customers = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'updatedAt' | 'createdAt' | 'name'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerRow | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<CustomerRow | null>(null);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page,
      limit,
      sortBy,
      sortOrder,
    };

    if (search.trim()) {
      params.search = search.trim();
    }

    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active';
    }

    return params;
  }, [page, limit, search, statusFilter, sortBy, sortOrder]);

  const fetchCustomers = useCallback(async () => {
    if (!token) {
      setRows([]);
      setTotal(0);
      setTotalPages(0);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/customers', {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
      });

      const customers = Array.isArray(response.data?.customers) ? response.data.customers : [];
      setRows(customers);
      setTotal(typeof response.data?.total === 'number' ? response.data.total : customers.length);
      setTotalPages(typeof response.data?.totalPages === 'number' ? response.data.totalPages : 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch customers.');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [token, queryParams]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const resetForm = () => {
    setEditingCustomer(null);
    setForm({ name: '', phone: '', email: '' });
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (customer: CustomerRow) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
    });
    setShowModal(true);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setError('Authentication is required. Please login again.');
      return;
    }

    if (!form.name.trim() || !form.phone.trim()) {
      setError('Name and phone are required.');
      return;
    }

    try {
      if (editingCustomer?._id) {
        await axios.put(
          `/customers/${editingCustomer._id}`,
          {
            name: form.name.trim(),
            phone: form.phone.trim(),
            email: form.email.trim() || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSuccess('Customer updated successfully.');
      } else {
        await axios.post(
          '/customers',
          {
            name: form.name.trim(),
            phone: form.phone.trim(),
            email: form.email.trim() || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSuccess('Customer created successfully.');
      }

      setShowModal(false);
      resetForm();
      await fetchCustomers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save customer.');
    }
  };

  const handleToggleStatus = async (customer: CustomerRow, nextStatus: boolean) => {
    if (!token) {
      setError('Authentication is required. Please login again.');
      return;
    }

    try {
      await axios.patch(
        `/customers/${customer._id}/status`,
        { isActive: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(nextStatus ? 'Customer activated.' : 'Customer deactivated.');
      await fetchCustomers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update status.');
    }
  };

  const handleDelete = async () => {
    if (!token || !deleteCandidate?._id) {
      return;
    }

    try {
      await axios.delete(`/customers/${deleteCandidate._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Customer deleted (deactivated) successfully.');
      setShowDeleteDialog(false);
      setDeleteCandidate(null);
      await fetchCustomers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete customer.');
    }
  };

  const canGoPrev = page > 1;
  const canGoNext = totalPages > 0 && page < totalPages;

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Customers</h4>
          <Button variant="dark" onClick={handleOpenCreate}>Add Customer</Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Form
            className="d-flex gap-2 flex-grow-1"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSearch(searchInput);
            }}
            style={{ minWidth: '260px' }}
          >
            <Form.Control
              placeholder="Search by name, phone, email"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <Button type="submit" variant="outline-dark">Search</Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
            >
              Clear
            </Button>
          </Form>

          <Form.Select
            value={statusFilter}
            onChange={(event) => {
              setPage(1);
              setStatusFilter(event.target.value as 'all' | 'active' | 'inactive');
            }}
            style={{ maxWidth: '160px' }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>

          <Form.Select
            value={sortBy}
            onChange={(event) => {
              setPage(1);
              setSortBy(event.target.value as 'updatedAt' | 'createdAt' | 'name');
            }}
            style={{ maxWidth: '180px' }}
          >
            <option value="updatedAt">Sort: Updated</option>
            <option value="createdAt">Sort: Created</option>
            <option value="name">Sort: Name</option>
          </Form.Select>

          <Form.Select
            value={sortOrder}
            onChange={(event) => {
              setPage(1);
              setSortOrder(event.target.value as 'asc' | 'desc');
            }}
            style={{ maxWidth: '120px' }}
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </Form.Select>

          <Form.Select
            value={limit}
            onChange={(event) => {
              setPage(1);
              setLimit(Number(event.target.value));
            }}
            style={{ maxWidth: '130px' }}
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </Form.Select>
        </div>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Orders</th>
              <th>Spent</th>
              <th>Last Order</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center">No customers found.</td>
              </tr>
            ) : (
              rows.map((customer, index) => (
                <tr key={customer._id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>{customer.name || '-'}</td>
                  <td>{customer.phone}</td>
                  <td>{customer.email || '-'}</td>
                  <td>
                    <div>{Number(customer.totalOrders ?? 0)}</div>
                    <small className="text-muted">Delivered: {Number(customer.deliveredOrders ?? 0)}</small>
                  </td>
                  <td>Rs {Number(customer.totalSpent ?? 0).toFixed(2)}</td>
                  <td>{customer.lastOrderAt ? new Date(customer.lastOrderAt).toLocaleDateString() : '-'}</td>
                  <td>{customer.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}</td>
                  <td>
                    <Button size="sm" className="me-2" variant="warning" onClick={() => handleOpenEdit(customer)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      className="me-2"
                      variant={customer.isActive ? 'secondary' : 'success'}
                      onClick={() => handleToggleStatus(customer, !customer.isActive)}
                    >
                      {customer.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => { setDeleteCandidate(customer); setShowDeleteDialog(true); }}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center">
          <small>Showing {rows.length} of {total} customers</small>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="outline-secondary" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>
              Prev
            </Button>
            <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
            <Button size="sm" variant="outline-secondary" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
              Next
            </Button>
          </div>
        </div>
      </Card>

      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Customer name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                placeholder="+923001234567"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email (optional)</Form.Label>
              <Form.Control
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="customer@example.com"
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button variant="dark" type="submit">
                {editingCustomer ? 'Update Customer' : 'Create Customer'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteDialog} onHide={() => { setShowDeleteDialog(false); setDeleteCandidate(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">You are about to delete this customer:</p>
          <p className="mb-0"><strong>{deleteCandidate?.name || deleteCandidate?.phone || 'Selected Customer'}</strong></p>
          {deleteCandidate?.phone ? <small className="text-muted">{deleteCandidate.phone}</small> : null}
          <div className="mt-2 text-muted">This action performs soft delete (deactivates customer) to preserve order records.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteDialog(false); setDeleteCandidate(null); }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Customers;
